import { normalizeAssetImagesToWireStrings } from '../api/assetPayloadNormalize';
import { assetRecordFirstImageSrc } from '../components/AssetGetByIdView';
import { assetRecordPrimaryId } from './assetRecordPrimaryId';
import { localeTagForAppLanguage } from '../i18n/languages';
import type { AppLanguage } from '../i18n/types';
import { isoToCalendarDayKey } from './assetBookingCalendar';
import {
  catalogThumbForAssetId,
  loadBookingListAssetThumbnails,
  normalizeAssetLookupKey,
} from './bookingAssetThumbnailCache';
import { registeredAssetThumbnailById } from './assetThumbnailStore';
import {
  prepareRecordForThumbnailLookup,
  readRecordImagesWireValue,
  resolveAssetImageSrc,
  wirePathToImageDisplaySrc,
} from './assetImageResolve';

export { loadBookingListAssetThumbnails };

const BOOKING_ROW_IMAGE_SCALAR_KEYS = [
  'asset_image_url',
  'asset_image',
  'asset_thumbnail',
  'asset_thumbnail_url',
  'facility_image',
  'facility_image_url',
  'thumbnail_url',
  'image_url',
  'ImageUrl',
  'main_image',
  'cover_image',
] as const;

function resolveImageScalar(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  const t = raw.trim();
  return wirePathToImageDisplaySrc(t) ?? resolveAssetImageSrc(t) ?? null;
}

/** Best thumbnail URL from an asset or booking row (main image first). */
export function resolveBookingAssetThumbnailSrc(record: Record<string, unknown>): string | null {
  for (const key of BOOKING_ROW_IMAGE_SCALAR_KEYS) {
    const src = resolveImageScalar(record[key]);
    if (src) {
      return src;
    }
  }

  const prepared = prepareRecordForThumbnailLookup(record);
  const fromRecord = assetRecordFirstImageSrc(prepared);
  if (fromRecord) {
    return fromRecord;
  }

  const wires = normalizeAssetImagesToWireStrings(readRecordImagesWireValue(record));
  for (const wire of wires) {
    const src = resolveImageScalar(wire);
    if (src) {
      return src;
    }
  }

  return null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function bookingPricingTypeName(booking: Record<string, unknown>): string {
  const candidates = [
    booking.pricing_type_name,
    booking.pricing_type,
    booking.rate_type,
    booking.payment_option,
  ];
  for (const raw of candidates) {
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim().toLowerCase();
    }
  }
  return '';
}

/** True when the booking span should show clock times (hourly / same-day timed). */
export function bookingRecordIsHourly(booking: Record<string, unknown>): boolean {
  const typeName = bookingPricingTypeName(booking);
  if (/\bhour/.test(typeName) || typeName === 'hr' || typeName === 'hrs' || typeName === 'hourly') {
    return true;
  }

  const startIso = booking.start_date;
  const endIso = booking.end_date;
  if (typeof startIso !== 'string' || typeof endIso !== 'string') {
    return false;
  }
  const start = new Date(startIso.trim());
  const end = new Date(endIso.trim());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
    return false;
  }

  const startKey = isoToCalendarDayKey(startIso);
  const endKey = isoToCalendarDayKey(endIso);
  if (!startKey || !endKey || startKey !== endKey) {
    return false;
  }

  const durationMs = end.getTime() - start.getTime();
  if (durationMs >= 24 * 60 * 60 * 1000) {
    return false;
  }

  const startAtMidnight = start.getHours() === 0 && start.getMinutes() === 0;
  const endAtDayEnd = end.getHours() === 23 && end.getMinutes() >= 59;
  return !(startAtMidnight && endAtDayEnd);
}

function intlLocale(language?: AppLanguage): string | undefined {
  return language ? localeTagForAppLanguage(language) : undefined;
}

export function formatBookingListTime(iso: unknown, language?: AppLanguage): string | null {
  if (typeof iso !== 'string' || !iso.trim()) {
    return null;
  }
  const dt = new Date(iso.trim());
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(intlLocale(language), { timeStyle: 'short' }).format(dt);
}

export function bookingRecordScheduleLabel(
  booking: Record<string, unknown>,
  language?: AppLanguage,
): string {
  const startIso = booking.start_date;
  const endIso = booking.end_date;
  if (typeof startIso !== 'string' || typeof endIso !== 'string') {
    return bookingRecordDateRangeLabel(booking, language);
  }

  const start = new Date(startIso.trim());
  const end = new Date(endIso.trim());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return bookingRecordDateRangeLabel(booking, language);
  }

  const startKey = isoToCalendarDayKey(startIso);
  const endKey = isoToCalendarDayKey(endIso);
  const locale = intlLocale(language);
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'long' });
  const timeFmt = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });

  if (startKey && endKey && startKey === endKey) {
    const datePart = dateFmt.format(start);
    const startTime = timeFmt.format(start);
    const endTime = timeFmt.format(end);
    return `${datePart}, ${startTime} – ${endTime}`;
  }

  const startDate = formatBookingListDate(startIso, language);
  const endDate = formatBookingListDate(endIso, language);
  const startTime = formatBookingListTime(startIso, language);
  const endTime = formatBookingListTime(endIso, language);
  if (startDate && endDate && startTime && endTime) {
    return `${startDate} ${startTime} – ${endDate} ${endTime}`;
  }
  return bookingRecordDateRangeLabel(booking, language);
}

/** Display name for a booking list row — prefer linked asset name. */
export function bookingRecordAssetName(booking: Record<string, unknown>): string {
  return (
    (typeof booking.asset_name === 'string' && booking.asset_name.trim()) ||
    (typeof booking.name === 'string' && booking.name.trim()) ||
    (typeof booking.asset_id === 'string' && booking.asset_id.trim()) ||
    'Booking'
  );
}

export function formatBookingListDate(iso: unknown, language?: AppLanguage): string | null {
  if (typeof iso !== 'string' || !iso.trim()) {
    return null;
  }
  const dt = new Date(iso.trim());
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(intlLocale(language), { dateStyle: 'long' }).format(dt);
}

/** Relative “starts in …” label for notification rows. */
export function formatBookingStartsInLabel(
  booking: Record<string, unknown>,
  language?: AppLanguage,
  nowMs: number = Date.now(),
): string | null {
  const raw = booking.start_date ?? booking.startDate;
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  const startMs = Date.parse(raw.trim());
  if (Number.isNaN(startMs) || startMs <= nowMs) {
    return null;
  }
  const diffMs = startMs - nowMs;
  const locale = intlLocale(language);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const minutesTotal = Math.max(1, Math.round(diffMs / (60 * 1000)));
  if (minutesTotal < 60) {
    return rtf.format(minutesTotal, 'minute');
  }
  const hoursTotal = Math.round(diffMs / (60 * 60 * 1000));
  if (hoursTotal < 24) {
    return rtf.format(hoursTotal, 'hour');
  }
  const daysTotal = Math.round(diffMs / (24 * 60 * 60 * 1000));
  return rtf.format(daysTotal, 'day');
}

/** Booked span for list rows — includes times for hourly bookings. */
export function bookingRecordDateRangeLabel(
  booking: Record<string, unknown>,
  language?: AppLanguage,
): string {
  if (bookingRecordIsHourly(booking)) {
    return bookingRecordScheduleLabel(booking, language);
  }
  const start = formatBookingListDate(booking.start_date, language);
  const end = formatBookingListDate(booking.end_date, language);
  if (start && end) {
    return start === end ? start : `${start} – ${end}`;
  }
  return start || end || '—';
}

function coerceBookingAssetId(raw: unknown): string | null {
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

/** Linked asset object on a booking list row, when the API embeds it. */
export function bookingRecordNestedAsset(
  booking: Record<string, unknown>,
): Record<string, unknown> | null {
  const nested = booking.asset ?? booking.Asset ?? booking.facility ?? booking.Facility;
  return isPlainObject(nested) ? nested : null;
}

export function bookingRecordAssetId(booking: Record<string, unknown>): string | null {
  const nested = bookingRecordNestedAsset(booking);
  if (nested) {
    const fromNested =
      assetRecordPrimaryId(nested) ??
      coerceBookingAssetId(
        nested.id ??
          nested.Id ??
          nested.asset_id ??
          nested.assetId ??
          nested.AssetId ??
          nested.facility_id ??
          nested.FacilityId,
      );
    if (fromNested) {
      return fromNested;
    }
  }
  return coerceBookingAssetId(
    booking.asset_id ??
      booking.assetId ??
      booking.AssetId ??
      booking.facility_id ??
      booking.FacilityId,
  );
}

/** Thumbnail URL when the booking row already embeds image paths/URLs. */
export function bookingRecordEmbeddedImageSrc(booking: Record<string, unknown>): string | null {
  const nested = bookingRecordNestedAsset(booking);
  if (nested) {
    const fromNested = resolveBookingAssetThumbnailSrc(nested);
    if (fromNested) {
      return fromNested;
    }
  }
  return resolveBookingAssetThumbnailSrc(booking);
}

/** Unique asset ids that still need a thumbnail fetch (in-flight or completed keys are skipped). */
export function uniqueBookingListAssetIdsForThumbs(
  bookings: Record<string, unknown>[],
  requestedKeys: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of bookings) {
    const id = bookingRecordAssetId(row);
    if (!id) {
      continue;
    }
    const keys = [id.toLowerCase(), normalizeAssetLookupKey(id)];
    if (keys.some((key) => seen.has(key) || requestedKeys.has(key))) {
      continue;
    }
    for (const key of keys) {
      seen.add(key);
    }
    out.push(id);
  }
  return out;
}

/** @deprecated Use {@link uniqueBookingListAssetIdsForThumbs}. */
export function uniqueBookingListAssetIdsNeedingImages(
  bookings: Record<string, unknown>[],
  requestedKeys: ReadonlySet<string>,
): string[] {
  return uniqueBookingListAssetIdsForThumbs(bookings, requestedKeys);
}

export function bookingRowThumbSrc(
  row: Record<string, unknown>,
  thumbByAssetId: ReadonlyMap<string, string | null>,
): string | null {
  const embedded = bookingRecordEmbeddedImageSrc(row);
  const assetId = bookingRecordAssetId(row);
  if (assetId) {
    const fromStore = registeredAssetThumbnailById(assetId);
    if (fromStore) {
      return fromStore;
    }
    const fetched = catalogThumbForAssetId(thumbByAssetId, assetId);
    if (fetched) {
      return fetched;
    }
  }
  return embedded;
}

export function isAssetRecordPayload(v: unknown): v is Record<string, unknown> {
  return isPlainObject(v);
}
